import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, BACKEND_URL, fmtDuration } from "@/lib/api";
import { PortalShell } from "@/pages/ClientPortalShell";
import {
  Upload,
  Film,
  Trash2,
  Play,
  ArrowRight,
  Loader2,
  Download,
  Scissors,
  X,
  Lock,
  Sparkles,
  Share2,
  Pencil,
  Search,
  Presentation,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { ShareSheet } from "@/components/ShareSheet";
import { FolderSidebar, MoveToFolderMenu, useFolders, filterByFolder } from "@/components/Folders";
import CoachingModal from "@/components/CoachingModal";
import CoachingBanner from "@/components/CoachingBanner";
import DeepScanModal from "@/components/DeepScanModal";

const TIER_RANK = { free: 0, storage: 1, creator: 2, pro: 3 };

export default function MyStudio() {
  const [videos, setVideos] = useState([]);
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewClip, setPreviewClip] = useState(null);
  const [shareClip, setShareClip] = useState(null);
  const [coachClip, setCoachClip] = useState(null);
  const [scanVideo, setScanVideo] = useState(null);
  const [sub, setSub] = useState({ tier: "free", storage_used_bytes: 0, storage_limit_bytes: 0 });
  const fileRef = useRef(null);
  const navigate = useNavigate();

  // Folder state (two independent contexts: videos + clips)
  const videoFolders = useFolders("client_videos");
  const clipFolders = useFolders("client_clips");
  const [videoActive, setVideoActive] = useState("all");   // "all" | "unfiled" | folder.id
  const [clipActive, setClipActive] = useState("all");
  const [videoSearch, setVideoSearch] = useState("");
  const [clipSearch, setClipSearch] = useState("");

  const canUpload = TIER_RANK[sub.tier] >= 1; // storage+
  const canCut = TIER_RANK[sub.tier] >= 2;    // creator+
  const canCoach = TIER_RANK[sub.tier] >= 3;  // pro

  const load = async () => {
    setLoading(true);
    try {
      const [v, c, s] = await Promise.all([
        api.get("/box/videos"),
        api.get("/box/clips"),
        api.get("/subscriptions/me"),
      ]);
      setVideos(v.data.videos || []);
      setClips(c.data.clips || []);
      setSub(s.data);
    } catch {
      toast.error("Failed to load Rodeo Box");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const probeDuration = (file) =>
    new Promise((resolve) => {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = URL.createObjectURL(file);
      v.onloadedmetadata = () => {
        const d = v.duration;
        URL.revokeObjectURL(v.src);
        resolve(isFinite(d) ? d : 0);
      };
      v.onerror = () => resolve(0);
    });

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (uploading) return;
    setUploading(true);
    setProgress(0);
    let videoId = null;
    try {
      const duration = await probeDuration(file);
      const init = await api.post("/box/videos/initiate-upload", {
        filename: file.name,
        file_size: file.size,
        content_type: file.type || "video/mp4",
        duration,
      });
      videoId = init.data.video_id;
      const { chunk_size, total_parts } = init.data;
      const parts = [];
      for (let i = 0; i < total_parts; i++) {
        const partNumber = i + 1;
        const start = i * chunk_size;
        const end = Math.min(start + chunk_size, file.size);
        const blob = file.slice(start, end);
        const urlRes = await api.post("/box/videos/upload-part-url", {
          video_id: videoId,
          part_number: partNumber,
        });
        const putRes = await fetch(urlRes.data.url, { method: "PUT", body: blob });
        if (!putRes.ok) throw new Error(`Part ${partNumber} upload failed: ${putRes.status}`);
        const etag = (putRes.headers.get("etag") || putRes.headers.get("ETag") || "").replaceAll('"', "");
        if (!etag) throw new Error(`Part ${partNumber} missing ETag`);
        parts.push({ PartNumber: partNumber, ETag: etag });
        setProgress(Math.round(((i + 1) / total_parts) * 100));
      }
      await api.post("/box/videos/complete-upload", {
        video_id: videoId,
        parts,
        duration,
      });
      toast.success("Uploaded — ready to cut");
      load();
    } catch (err) {
      toast.error("Upload failed: " + (err?.response?.data?.detail || err.message));
      if (videoId) {
        api.post(`/box/videos/${videoId}/abort-upload`).catch(() => {});
      }
    } finally {
      setUploading(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const delVideo = async (v) => {
    if (!window.confirm(`Delete "${v.original_name}" and all its clips?`)) return;
    try {
      await api.delete(`/box/videos/${v.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const delClip = async (c) => {
    if (!window.confirm("Delete this clip?")) return;
    try {
      await api.delete(`/box/clips/${c.id}`);
      toast.success("Clip deleted");
      load();
    } catch {
      toast.error("Failed to delete clip");
    }
  };

  const renameVideo = async (v) => {
    const next = window.prompt("Rename video", v.original_name || "");
    if (next == null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === (v.original_name || "").trim()) return;
    try {
      await api.patch(`/box/videos/${v.id}`, { original_name: trimmed });
      toast.success("Renamed");
      load();
    } catch (e) {
      toast.error("Rename failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  const renameClip = async (c) => {
    const cur = c.sub_label || "";
    const next = window.prompt("Rename clip", cur);
    if (next == null) return;
    const trimmed = next.trim();
    if (trimmed === cur.trim()) return;
    try {
      await api.patch(`/box/clips/${c.id}`, { sub_label: trimmed });
      toast.success("Renamed");
      load();
    } catch (e) {
      toast.error("Rename failed: " + (e?.response?.data?.detail || e.message));
    }
  };

  // Apply folder filter → then text search
  const visibleVideos = useMemo(() => {
    const byFolder = filterByFolder(videos, videoActive);
    const q = videoSearch.trim().toLowerCase();
    if (!q) return byFolder;
    return byFolder.filter((v) => (v.original_name || "").toLowerCase().includes(q));
  }, [videos, videoActive, videoSearch]);

  const visibleClips = useMemo(() => {
    const byFolder = filterByFolder(clips, clipActive);
    const q = clipSearch.trim().toLowerCase();
    if (!q) return byFolder;
    return byFolder.filter(
      (c) => (c.sub_label || "").toLowerCase().includes(q) ||
             (c.source_video_name || "").toLowerCase().includes(q),
    );
  }, [clips, clipActive, clipSearch]);

  // After any folder move we must refresh both the items (folder_id changed)
  // and the folder counts.
  const reloadVideosAndFolders = async () => { await load(); videoFolders.reload(); };
  const reloadClipsAndFolders = async () => { await load(); clipFolders.reload(); };

  return (
    <PortalShell>
      <div className="border-l-4 border-orange-600 pl-5 mb-8" data-testid="rodeo-box-header">
        <div className="label-tiny mb-2">Rodeo Box</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Your Studio</h1>
        <p className="text-stone-400 mt-3">
          Drop your own footage here, trim the good runs into clips, and download them for social.
          Every clip you export carries a subtle &ldquo;Made with Rodeo Cut Studio&rdquo; corner badge.
        </p>
      </div>

      {/* Upload bar */}
      {!canUpload ? (
        <div className="border border-stone-800 p-8 mb-8 flex flex-col sm:flex-row items-center gap-4" data-testid="upload-gate">
          <Lock size={32} className="text-orange-500 shrink-0" />
          <div className="flex-1 text-center sm:text-left">
            <div className="font-display uppercase font-bold text-lg">Unlock Rodeo Box</div>
            <div className="text-xs text-stone-400 mt-1">
              Subscribe to any plan to upload your own videos, trim runs, and build reels.
            </div>
          </div>
          <button onClick={() => navigate("/portal/upgrade")} className="btn-primary inline-flex items-center gap-2 text-sm px-5 py-3" data-testid="upgrade-cta">
            <Sparkles size={13} /> See plans
          </button>
        </div>
      ) : (
        <div
          className={`border ${uploading ? "border-orange-600" : "border-stone-800 hover:border-stone-700"} p-6 mb-8 transition-colors`}
          data-testid="upload-bar"
        >
          {uploading ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="inline-flex items-center gap-2 font-display uppercase font-bold text-lg">
                  <Loader2 size={18} className="animate-spin text-orange-500" />
                  Uploading…
                </div>
                <div className="font-mono-tc text-orange-400">{progress}%</div>
              </div>
              <div className="h-2 bg-stone-900 border border-stone-800 overflow-hidden">
                <div className="h-full bg-orange-600 transition-all" style={{ width: `${progress}%` }} />
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <div className="font-display uppercase font-bold text-lg mb-1">Upload a video</div>
                <div className="text-xs text-stone-500">MP4, MOV, or any format — up to 25 GB. Uploaded straight to your private storage.</div>
                <StorageMeter sub={sub} />
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="video/*"
                onChange={onFilePick}
                className="hidden"
                data-testid="box-file-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn-primary inline-flex items-center gap-2 text-sm px-5 py-3"
                data-testid="box-upload-btn"
              >
                <Upload size={14} /> Choose video
              </button>
            </div>
          )}
        </div>
      )}

      {/* My Videos */}
      <SectionHeader title="Your videos" count={videos.length} />
      {loading ? (
        <div className="text-stone-500 py-10 text-center">Loading…</div>
      ) : videos.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-14 text-center mb-10" data-testid="empty-videos">
          <Film size={28} className="mx-auto text-stone-600 mb-3" />
          <p className="text-stone-400">No videos yet. Upload one to get started.</p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6 mb-10" data-testid="videos-section">
          <FolderSidebar
            scope="client_videos"
            active={videoActive}
            onChange={setVideoActive}
            folders={videoFolders.folders}
            unfiledCount={videoFolders.unfiledCount}
            total={videoFolders.total || videos.length}
            onReload={videoFolders.reload}
          />
          <div className="flex-1 min-w-0">
            <SearchBar
              value={videoSearch}
              onChange={setVideoSearch}
              placeholder="Search videos…"
              testId="videos-search"
            />
            {visibleVideos.length === 0 ? (
              <div className="border border-dashed border-stone-800 p-10 text-center text-stone-500 text-sm">
                Nothing here yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="videos-grid">
                {visibleVideos.map((v) => (
                  <VideoCard
                    key={v.id}
                    video={v}
                    canCut={canCut}
                    canCoach={canCoach}
                    folders={videoFolders.folders}
                    onDelete={() => delVideo(v)}
                    onRename={() => renameVideo(v)}
                    onUpgrade={() => navigate("/portal/upgrade")}
                    onMoved={reloadVideosAndFolders}
                    onDeepScan={() => canCoach ? setScanVideo(v) : navigate("/portal/upgrade")}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* My Clips */}
      <SectionHeader title="Your clips" count={clips.length} />
      {canCoach && (
        <CoachingBanner clips={clips} onTry={(c) => setCoachClip(c)} />
      )}
      {clips.length === 0 ? (
        <div className="border border-dashed border-stone-800 p-14 text-center" data-testid="empty-clips">
          <Scissors size={28} className="mx-auto text-stone-600 mb-3" />
          <p className="text-stone-400">Trim a video into clips &mdash; they&apos;ll show up here ready to share.</p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6" data-testid="clips-section">
          <FolderSidebar
            scope="client_clips"
            active={clipActive}
            onChange={setClipActive}
            folders={clipFolders.folders}
            unfiledCount={clipFolders.unfiledCount}
            total={clipFolders.total || clips.length}
            onReload={clipFolders.reload}
          />
          <div className="flex-1 min-w-0">
            <SearchBar
              value={clipSearch}
              onChange={setClipSearch}
              placeholder="Search clips…"
              testId="clips-search"
            />
            {visibleClips.length === 0 ? (
              <div className="border border-dashed border-stone-800 p-10 text-center text-stone-500 text-sm">
                Nothing here yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="clips-grid">
                {visibleClips.map((c) => (
                  <ClipCard
                    key={c.id}
                    clip={c}
                    folders={clipFolders.folders}
                    canCoach={canCoach}
                    onCoach={() => canCoach ? setCoachClip(c) : navigate("/portal/upgrade")}
                    onPreview={() => setPreviewClip(c)}
                    onDelete={() => delClip(c)}
                    onRename={() => renameClip(c)}
                    onShare={() => setShareClip(c)}
                    onMoved={reloadClipsAndFolders}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {previewClip && <ClipPreview clip={previewClip} onClose={() => setPreviewClip(null)} />}
      {coachClip && (
        <CoachingModal
          clip={coachClip}
          onClose={() => setCoachClip(null)}
          onSaved={() => { setCoachClip(null); load(); }}
        />
      )}
      {scanVideo && (
        <DeepScanModal
          video={scanVideo}
          onClose={() => setScanVideo(null)}
          onDone={() => { load(); }}
        />
      )}
      {shareClip && (
        <ShareSheet
          clipId={shareClip.id}
          title={shareClip.sub_label || "My rodeo clip"}
          downloadUrl={`${BACKEND_URL}/api/box/clips/${shareClip.id}/download`}
          onClose={() => setShareClip(null)}
        />
      )}
    </PortalShell>
  );
}

function SectionHeader({ title, count }) {
  return (
    <div className="flex items-baseline justify-between mb-4 border-b border-stone-800 pb-2">
      <h2 className="font-display uppercase text-2xl font-bold tracking-tight">{title}</h2>
      <span className="font-mono-tc text-xs text-stone-500">
        <span className="text-orange-500 text-base">{count}</span>
      </span>
    </div>
  );
}

function SearchBar({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative mb-3">
      <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-stone-950 border border-stone-800 focus:border-orange-600 pl-9 pr-8 py-2 text-sm text-stone-100 placeholder-stone-500 focus:outline-none"
        data-testid={testId}
      />
      {value && (
        <button
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-100"
          title="Clear"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

function StorageMeter({ sub }) {
  const gb = (b) => (b / 1024 ** 3);
  const used = gb(sub.storage_used_bytes || 0);
  const limit = gb(sub.storage_limit_bytes || 0);
  if (limit <= 0) return null;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const nearFull = pct >= 85;
  return (
    <div className="mt-3" data-testid="storage-meter">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-stone-500 mb-1">
        <span>Storage</span>
        <span className={`font-mono-tc ${nearFull ? "text-rose-400" : "text-orange-400"}`}>
          {used.toFixed(2)} / {limit.toFixed(0)} GB
        </span>
      </div>
      <div className="h-1 bg-stone-900 overflow-hidden">
        <div className={`h-full ${nearFull ? "bg-rose-500" : "bg-orange-600"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function VideoCard({ video, canCut, canCoach, folders, onDelete, onRename, onUpgrade, onMoved, onDeepScan }) {
  const ready = video.status === "ready";
  return (
    <div className="border border-stone-800 hover:border-orange-700 transition-colors" data-testid={`box-video-${video.id}`}>
      <Link
        to={ready && canCut ? `/portal/studio/${video.id}` : "#"}
        onClick={(e) => {
          if (!ready) return e.preventDefault();
          if (!canCut) { e.preventDefault(); onUpgrade(); }
        }}
        className="block aspect-video bg-black relative overflow-hidden group"
      >
        <div className="absolute inset-0 flex items-center justify-center text-stone-600">
          <Film size={40} />
        </div>
        {ready && canCut && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/50 transition-opacity">
            <Scissors size={30} className="text-white" />
          </div>
        )}
        {ready && !canCut && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/60 transition-opacity">
            <Lock size={26} className="text-orange-500" />
          </div>
        )}
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="text-center">
              <Loader2 size={22} className="mx-auto text-orange-500 animate-spin mb-1" />
              <div className="text-[10px] uppercase tracking-widest text-stone-400">
                {video.status}
              </div>
            </div>
          </div>
        )}
        {video.duration > 0 && (
          <div className="absolute bottom-1.5 right-1.5 font-mono-tc text-[10px] bg-black/70 px-1.5 py-0.5">
            {fmtDuration(video.duration)}
          </div>
        )}
      </Link>
      <div className="p-3 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 group/title">
            <div className="font-display uppercase font-bold truncate">{video.original_name}</div>
            <button
              onClick={onRename}
              className="opacity-0 group-hover/title:opacity-100 text-stone-500 hover:text-orange-400 shrink-0 transition-opacity"
              title="Rename video"
              data-testid={`box-video-rename-${video.id}`}
            >
              <Pencil size={11} />
            </button>
          </div>
          <div className="text-[11px] text-stone-500">{(video.uploaded_at || "").slice(0, 10)}</div>
        </div>
        {ready && (
          canCut ? (
            <Link
              to={`/portal/studio/${video.id}`}
              className="btn-primary !py-1 !px-2 text-[11px] inline-flex items-center gap-1"
              data-testid={`box-cut-${video.id}`}
            >
              Cut <ArrowRight size={11} />
            </Link>
          ) : (
            <button
              onClick={onUpgrade}
              className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1"
              data-testid={`box-cut-locked-${video.id}`}
            >
              <Lock size={10} /> Unlock
            </button>
          )
        )}
        {ready && (
          <button
            onClick={onDeepScan}
            className={`btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 ${canCoach ? "!text-orange-400" : ""}`}
            title={canCoach ? "AI Deep Scan — auto-detect every run and render each as a clip" : "Deep Scan · Pro plan"}
            data-testid={`box-deepscan-${video.id}`}
          >
            {canCoach ? <Zap size={11} /> : <Lock size={11} />} Deep Scan
          </button>
        )}
        <MoveToFolderMenu
          scope="client_videos"
          itemId={video.id}
          currentFolderId={video.folder_id}
          folders={folders || []}
          onMoved={onMoved}
        />
        <button onClick={onDelete} className="text-stone-500 hover:text-rose-400" data-testid={`box-video-del-${video.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function ClipCard({ clip, folders, canCoach, onPreview, onDelete, onRename, onShare, onCoach, onMoved }) {
  const isCoached = clip.kind === "coached";
  // Pulse only until the user tries coaching once (banner dismissed OR they
  // already have any coached clip locally-signalled via localStorage).
  const showCoachPulse = (() => {
    if (!canCoach || isCoached) return false;
    try { return localStorage.getItem("coaching_banner_dismissed_v1") !== "1"; } catch { return true; }
  })();
  return (
    <div className="border border-stone-800 hover:border-orange-700 transition-colors" data-testid={`box-clip-${clip.id}`}>
      <button
        type="button"
        onClick={onPreview}
        className="block w-full aspect-video bg-black relative overflow-hidden group"
      >
        {clip.stream_url && (
          <video src={clip.stream_url} muted preload="metadata" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition-opacity">
          <Play size={30} className="text-white" />
        </div>
        {clip.duration != null && (
          <div className="absolute bottom-1.5 right-1.5 font-mono-tc text-[10px] bg-black/70 px-1.5 py-0.5">
            {fmtDuration(clip.duration)}
          </div>
        )}
      </button>
      <div className="p-3 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 group/title">
            <div className="font-display uppercase font-bold truncate">
              {clip.sub_label || "Untitled clip"}
            </div>
            <button
              onClick={onRename}
              className="opacity-0 group-hover/title:opacity-100 text-stone-500 hover:text-orange-400 shrink-0 transition-opacity"
              title="Rename clip"
              data-testid={`box-clip-rename-${clip.id}`}
            >
              <Pencil size={11} />
            </button>
          </div>
          <div className="text-[11px] text-stone-500 truncate">{clip.source_video_name}</div>
        </div>
        <button
          onClick={onShare}
          className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1"
          title="Share to social"
          data-testid={`box-clip-share-${clip.id}`}
        >
          <Share2 size={11} />
        </button>
        {!isCoached && (
          <button
            onClick={onCoach}
            className={`relative btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 ${canCoach ? "!text-orange-400" : ""}`}
            title={canCoach ? "Record a coaching session on this clip" : "Coaching Studio — Pro plan"}
            data-testid={`box-clip-coach-${clip.id}`}
          >
            {canCoach ? <Presentation size={11} /> : <Lock size={11} />}
            {canCoach && showCoachPulse && (
              <span className="absolute -top-1 -right-1 flex h-2 w-2" aria-hidden="true">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
              </span>
            )}
          </button>
        )}
        <a
          href={`${BACKEND_URL}/api/box/clips/${clip.id}/download`}
          className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1"
          data-testid={`box-clip-dl-${clip.id}`}
        >
          <Download size={11} />
        </a>
        <MoveToFolderMenu
          scope="client_clips"
          itemId={clip.id}
          currentFolderId={clip.folder_id}
          folders={folders || []}
          onMoved={onMoved}
        />
        <button onClick={onDelete} className="text-stone-500 hover:text-rose-400" data-testid={`box-clip-del-${clip.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function ClipPreview({ clip, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()} data-testid="box-clip-preview">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display uppercase font-bold text-xl">{clip.sub_label || "Clip"}</div>
          <button onClick={onClose} className="text-stone-300 hover:text-white"><X size={20} /></button>
        </div>
        <video src={clip.stream_url} controls autoPlay className="w-full bg-black" />
        <div className="mt-3 flex items-center justify-end">
          <a
            href={`${BACKEND_URL}/api/box/clips/${clip.id}/download`}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            <Download size={13} /> Download
          </a>
        </div>
      </div>
    </div>
  );
}

// Discovery banner for Coaching Studio lives in /components/CoachingBanner.jsx —
// used both here and in ClientMyClips.

